import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output
} from '@angular/core';
import { FormsModule, UntypedFormGroup } from '@angular/forms';

import { BehaviorSubject, Observable, startWith, takeUntil } from 'rxjs';

import {
  DisplaySchema,
  MonthAbbrEnumMapping,
  MonthEnum,
  MonthOfYearToBitMapping,
  MonthsOfYear,
  OnlyExpandMonthOfYear,
  SummitDisposing,
  SummitIdObject,
  ViewField
} from '@summit/models';
import { ApiError } from '@summit/ui';
import { Button } from 'primeng/button';
import { SelectButton } from 'primeng/selectbutton';

@Component({
  selector: 's-form-input-month-of-year',
  templateUrl: './s-form-input-month-of-year.component.html',
  styleUrls: ['./s-form-input-month-of-year.component.scss'],
  standalone: true,
  imports: [FormsModule, SelectButton, Button],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class SFormInputMonthOfYearComponent<T extends SummitIdObject> extends SummitDisposing implements OnInit {
  @Input() label: string;
  @Input() form: UntypedFormGroup;
  @Input() fieldName: keyof T | string;
  @Input() groupName: string;
  @Input() apiError: ApiError;
  @Input() schema: DisplaySchema<T>;
  @Input() nullableLabel: string;
  @Input() displayHint: string;
  @Input() multiPick = true;
  @Input() NotForm = false;
  @Input() toggle = false;
  @Input() disableMonths: Array<number> = [];
  @Input() groupButtons = true;
  @Input() defaultSelectedMonth: Array<number> = [];
  @Input() resetButton = false;
  @Input() writeAsArray = false;
  @Output() changeEvent = new EventEmitter<number | undefined>();
  attribute?: ViewField<T>;
  selectedMonths: number[] = [];
  selectedMonth?: number;
  monthsOfYear: { id: string; name: string; code: MonthEnum; disabled: boolean }[] = [];
  firstRowMonths: { id: string; name: string; code: MonthEnum; disabled: boolean }[] = [];
  secondRowMonths: { id: string; name: string; code: MonthEnum; disabled: boolean }[] = [];
  arr: number[] = [];
  //added for notForm
  selectedMonthsSubject = new BehaviorSubject<number[]>([]);
  emittedMonths: Observable<number[]> = this.selectedMonthsSubject.asObservable();

  constructor(private cd: ChangeDetectorRef) {
    super();
  }

  ngOnInit() {
    // Create months array with abbreviated names
    this.monthsOfYear = [
      {
        id: '1',
        name: MonthAbbrEnumMapping[MonthEnum.January],
        code: MonthEnum.January,
        disabled: this.disableMonths.includes(MonthEnum.January)
      },
      {
        id: '2',
        name: MonthAbbrEnumMapping[MonthEnum.February],
        code: MonthEnum.February,
        disabled: this.disableMonths.includes(MonthEnum.February)
      },
      {
        id: '3',
        name: MonthAbbrEnumMapping[MonthEnum.March],
        code: MonthEnum.March,
        disabled: this.disableMonths.includes(MonthEnum.March)
      },
      {
        id: '4',
        name: MonthAbbrEnumMapping[MonthEnum.April],
        code: MonthEnum.April,
        disabled: this.disableMonths.includes(MonthEnum.April)
      },
      {
        id: '5',
        name: MonthAbbrEnumMapping[MonthEnum.May],
        code: MonthEnum.May,
        disabled: this.disableMonths.includes(MonthEnum.May)
      },
      {
        id: '6',
        name: MonthAbbrEnumMapping[MonthEnum.June],
        code: MonthEnum.June,
        disabled: this.disableMonths.includes(MonthEnum.June)
      },
      {
        id: '7',
        name: MonthAbbrEnumMapping[MonthEnum.July],
        code: MonthEnum.July,
        disabled: this.disableMonths.includes(MonthEnum.July)
      },
      {
        id: '8',
        name: MonthAbbrEnumMapping[MonthEnum.August],
        code: MonthEnum.August,
        disabled: this.disableMonths.includes(MonthEnum.August)
      },
      {
        id: '9',
        name: MonthAbbrEnumMapping[MonthEnum.September],
        code: MonthEnum.September,
        disabled: this.disableMonths.includes(MonthEnum.September)
      },
      {
        id: '10',
        name: MonthAbbrEnumMapping[MonthEnum.October],
        code: MonthEnum.October,
        disabled: this.disableMonths.includes(MonthEnum.October)
      },
      {
        id: '11',
        name: MonthAbbrEnumMapping[MonthEnum.November],
        code: MonthEnum.November,
        disabled: this.disableMonths.includes(MonthEnum.November)
      },
      {
        id: '12',
        name: MonthAbbrEnumMapping[MonthEnum.December],
        code: MonthEnum.December,
        disabled: this.disableMonths.includes(MonthEnum.December)
      }
    ];

    // Split months into two rows of 6 each
    this.firstRowMonths = this.monthsOfYear.slice(0, 6); // Jan-Jun
    this.secondRowMonths = this.monthsOfYear.slice(6, 12); // Jul-Dec

    if (this.schema) {
      this.attribute = this.schema?.getAttribute(this.fieldName as keyof T & string, this.groupName);
      if (!this.attribute) {
        console.error(
          `Could not find Property: ${this.fieldName?.toString()} in group ${this.groupName} in Tree:`,
          this.schema
        );
      }

      this.label = this.label ?? this.attribute?.displayName;

      // Get previously selected months
      if (this.form && this.fieldName) {
        this.form.controls[this.fieldName.toString()].valueChanges
          .pipe(startWith(this.form.controls[this.fieldName.toString()].value), takeUntil(this.disposing$))
          .subscribe((value) => {
            if (Array.isArray(value)) {
              this.selectedMonths = value.length > 0 ? value : [];
              this.selectedMonth = value.length > 0 ? this.selectedMonths[0] : undefined;
              this.cd.detectChanges();
            } else {
              const valueInt = parseInt(value, 10);
              this.selectedMonths = OnlyExpandMonthOfYear(valueInt);
              this.selectedMonth = this.selectedMonths.length > 0 ? this.selectedMonths[0] : undefined;
              this.cd.detectChanges();
            }
          });
      }
    }

    // If single month selection, get previously selected month
    if (!this.multiPick) {
      this.selectedMonth = this.selectedMonths[0];
    }

    if (this.defaultSelectedMonth.length > 0) {
      this.selectedMonths = this.defaultSelectedMonth;
    }
  }

  selectAllGroupE($event) {
    $event.stopPropagation();
    $event.preventDefault();
    const allMonths = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];
    this.selectedMonths = [];
    allMonths.forEach((month) => {
      if (this.disableMonths.findIndex((disMonth) => disMonth == month) == -1) this.selectedMonths.push(month);
    });
    this.onChange();
  }

  selectQuarterlyGroupE($event) {
    $event.stopPropagation();
    $event.preventDefault();
    const quarterlyMonths = [1, 4, 7, 10]; // Jan, Apr, Jul, Oct
    this.selectedMonths = [];
    quarterlyMonths.forEach((month) => {
      if (this.disableMonths.findIndex((disMonth) => disMonth == month) == -1) this.selectedMonths.push(month);
    });
    this.onChange();
  }

  clearGroupE($event) {
    $event.stopPropagation();
    $event.preventDefault();
    this.selectedMonths = [];
    this.onChange();
  }

  onChange($event?) {
    // Handle the selected month changes
    if (this.multiPick) {
      this.selectedMonths = $event?.value ?? this.selectedMonths;
    } else {
      this.selectedMonth = $event?.value;
      if (this.toggle && this.selectedMonth === undefined) return; // clear
      // standardized to array for rest of function
      this.selectedMonths = this.selectedMonth || this.selectedMonth === 0 ? [this.selectedMonth] : [];
    }
    if (this.NotForm) {
      this.selectedMonthsSubject.next([...this.selectedMonths]);
    }

    if (Number.isInteger(this.selectedMonths)) {
      this.arr = [this.selectedMonths as unknown as number];
    } else {
      this.arr = this.selectedMonths;
    }
    const bitMonthNo = this.arr.map((monthNo) => MonthOfYearToBitMapping[monthNo]);
    const newValue = bitMonthNo.length == 0 ? undefined : this.ReduceMonthOfYear(bitMonthNo);
    if (this.NotForm === false) {
      if (this.writeAsArray) {
        const newFormValue = OnlyExpandMonthOfYear(newValue ?? MonthsOfYear.None);
        this.form?.controls[this.fieldName.toString()].setValue(newFormValue);
      } else {
        const newFormValue = newValue ?? MonthsOfYear.None;
        this.form?.controls[this.fieldName.toString()].setValue(newFormValue);
      }
    }
    this.changeEvent.emit(newValue);
    if (this.toggle) {
      this.selectedMonths = [];
      this.selectedMonth = undefined;
    }
    this.cd.detectChanges();
  }

  ReduceMonthOfYear(input: MonthsOfYear[]) {
    const selectedMonthsNum: number = input.reduce(function (a, b) {
      return a | b;
    }, 0);
    return selectedMonthsNum;
  }

  resetGroup() {
    if (this.defaultSelectedMonth.length > 0) this.selectedMonths = this.defaultSelectedMonth;
    else this.selectedMonths = [];
  }

  public reset() {
    this.selectedMonth = undefined;
    this.selectedMonths = [];
    this.onChange();
  }
}
